import type { NodeV2 } from '@vedalib/editor'
import { NodeAnimationTypeUiEnum } from '@vedalib/editor'
import type { TargetAndTransition, Target, Transition } from 'framer-motion'
import * as R from 'ramda'
import { useMemo } from 'react'

import { PreviewMode } from 'services/Store/Project/enums'
import { useProjectContext } from 'services/Store/Project/hooks'
import { getNodeState, getPreviewMode } from 'services/Store/Project/selectors'

import { applyAnimation, DEFAULT_VALUE } from './utils'

type MotionTargetAdapterType = Omit<Target, `--${string}`>
type MotionTargetAndTransitionAdapterType = Omit<TargetAndTransition, `--${string}`>
type ReturnType = {
  initial?: MotionTargetAdapterType
  animate?: MotionTargetAndTransitionAdapterType | false
  whileHover?: MotionTargetAndTransitionAdapterType
  whileTap?: MotionTargetAndTransitionAdapterType
  whileInView?: MotionTargetAndTransitionAdapterType
  transition?: Transition
  viewport?: { once: boolean }
  onClick?: () => void
}

export const useNodeAnimation = (node: NodeV2): ReturnType | null => {
  const state = useProjectContext(getNodeState, node.id)
  const isEditor = useProjectContext(getPreviewMode) === PreviewMode.editor
  const isSpring = node.transform?.transition.type === 'spring'

  const initial = useMemo(
    () => ({
      ...DEFAULT_VALUE,
      ...(R.pickBy(R.isNotNil, node.transform?.value[0]?.data) as Record<string, number>),
      transformOrigin: node.transform?.transformOrigin || 'center',
    }),
    [node.transform?.transformOrigin, node.transform?.value],
  )

  const animate = useMemo(
    () => ({
      ...applyAnimation(node.transform?.value, isSpring),
      transformOrigin: node.transform?.transformOrigin || 'center',
    }),
    [node.transform?.value, node.transform?.transformOrigin, isSpring],
  )

  if (!node.transform) {
    return null
  }

  if (isEditor) {
    if (state?.playing) {
      return {
        initial,
        animate,
        transition: {
          ...node.transform?.transition,
          repeat: Number(node.transform?.transition?.repeat) || 0,
        },
      }
    }
    return { animate: false }
  }

  if (node.transform.type === NodeAnimationTypeUiEnum.CONST) {
    return {
      initial,
      animate,
      transition: {
        ...node.transform.transition,
        repeat: Number(node.transform.transition?.repeat) || Infinity,
        repeatType: 'reverse',
      },
    }
  }

  if (node.transform.type === NodeAnimationTypeUiEnum.TAP) {
    return {
      initial,
      whileTap: animate,
      transition: {
        ...node.transform.transition,
        repeat: Number(node.transform.transition?.repeat) || 0,
      },
    }
  }

  if (node.transform.type === NodeAnimationTypeUiEnum.HOVER) {
    return {
      initial,
      whileHover: animate,
      transition: {
        ...node.transform.transition,
        repeat: Number(node.transform.transition?.repeat) || 0,
      },
    }
  }

  if (node.transform.type === NodeAnimationTypeUiEnum.IN_VIEW) {
    return {
      initial,
      whileInView: animate,
      transition: {
        ...node.transform.transition,
        repeat: Number(node.transform.transition?.repeat) || 0,
      },
      viewport: { once: true },
    }
  }

  return null
}
